import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { PMSelectMenu } from "../../utils/PMFilterPopover";

const sameRect = (a, b) => a && b && a.top === b.top && a.left === b.left && a.bottom === b.bottom && a.right === b.right;

/**
 * Multi-select project-hour facet using Promang's shared filter menu.
 *
 * The menu stays open while several values are toggled. It is non-modal and
 * follows its trigger: picking a value re-filters the page (metric cards,
 * chips and the list change height) and the user may scroll, so the anchor is
 * re-measured instead of leaving the fixed menu at stale coordinates.
 * Escape returns focus to the trigger; tabbing out of the menu closes it.
 */
export function ProjectTimeFilterPicker({ label, options, selected, onChange, single = false, dark = false }) {
  const { t } = useTranslation();
  const triggerRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const open = Boolean(anchorRect);
  const selectedIds = selected.map(String);

  const close = useCallback(() => setAnchorRect(null), []);
  const remeasure = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      setAnchorRect(null);
      return;
    }
    setAnchorRect((previous) => (previous && !sameRect(previous, rect) ? rect : previous));
  }, []);

  // Re-anchor after every render while open (selection changes reflow the page).
  useLayoutEffect(() => {
    if (open) remeasure();
  });

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("scroll", remeasure, true);
    window.addEventListener("resize", remeasure);
    return () => {
      window.removeEventListener("scroll", remeasure, true);
      window.removeEventListener("resize", remeasure);
    };
  }, [open, remeasure]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`pto-picker-trigger${selectedIds.length ? " is-active" : ""}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(event) => setAnchorRect(open ? null : event.currentTarget.getBoundingClientRect())}
      >
        {label}
        {selectedIds.length ? <span className="pto-picker-count">{selectedIds.length}</span> : null}
        <ChevronDownIcon aria-hidden="true" className="h-4 w-4" />
      </button>
      {open && (
        <PMSelectMenu
          anchorRect={anchorRect}
          onClose={close}
          modal={false}
          ignoreOutsideRef={triggerRef}
          returnFocusRef={triggerRef}
          role="dialog"
          ariaLabel={label}
          options={(options || []).map((option) => ({ ...option, id: String(option.value) }))}
          value={single ? selectedIds[0] || null : selectedIds}
          onPick={(id) => {
            const nextId = String(id);
            onChange(single
              ? selectedIds.includes(nextId) ? [] : [nextId]
              : selectedIds.includes(nextId)
                ? selectedIds.filter((value) => value !== nextId)
                : [...selectedIds, nextId]);
          }}
          multi={!single}
          label={label}
          width={280}
          className={dark ? "pto-shared-filter-menu pto-shared-filter-menu--dark" : "pto-shared-filter-menu"}
          searchPlaceholder={t("project.time_overview.ui.search_filter", { defaultValue: "Søk {{label}}", label: label.toLocaleLowerCase() })}
          emptyLabel={t("project.time_overview.ui.no_matches", { defaultValue: "Ingen treff" })}
          renderOption={(option) => (
            <span className={`pto-picker-option-content${option.count ? "" : " is-empty"}`}>
              <span className="truncate">{option.label}</span>
              <small>{option.count}</small>
            </span>
          )}
        />
      )}
    </>
  );
}
